import { useMemo } from 'react';

import { normalizeRoles, PORTAL_ROLES } from '@core/access/capabilities';
import { useSettingsStore } from '@core/state/settings.store';
import { useUserStore } from '@core/state/user.store';
import { useWorkspaceStore } from '@core/state/workspace.store';

import { scopeChain, scopeTree, userScopeId, type ScopeNode } from './scopes';
import type { RoleDefault, SettingsDocument } from './settings.model';

/** Every scope: the built-in tree and the countries added in settings. */
export function scopesOf(document: SettingsDocument): ScopeNode[] {
  return scopeTree(document.countries);
}

/** The signed-in user's scope, Global first - their country claim, else their service line. */
export function useUserScopeChain(): ScopeNode[] {
  const countries = useSettingsStore((state) => state.document.countries);
  const region = useUserStore((state) => state.userData?.region);
  const workspace = useWorkspaceStore((state) => state.workspace);

  return useMemo(() => {
    const nodes = scopeTree(countries);
    return scopeChain(nodes, userScopeId(nodes, region, workspace));
  }, [countries, region, workspace]);
}

/** The same, outside React - for the transports and profile lookups. */
export function userScopeChainNow(regionClaim: string | null | undefined): ScopeNode[] {
  const { countries } = useSettingsStore.getState().document;
  const nodes = scopeTree(countries);

  return scopeChain(nodes, userScopeId(nodes, regionClaim, useWorkspaceStore.getState().workspace));
}

/** Most senior first; the first of a user's roles with a default set wins. */
const ROLE_PRECEDENCE = PORTAL_ROLES;

/** The defaults for the signed-in user's most senior role that has any. */
export function useRoleDefault(): RoleDefault {
  const roles = useUserStore((state) => state.userData?.roles);
  const defaults = useSettingsStore((state) => state.document.roleDefaults);

  return useMemo(() => {
    const held = normalizeRoles(roles);
    const role = ROLE_PRECEDENCE.find((candidate) => held.has(candidate) && defaults[candidate]);

    return role ? defaults[role] : {};
  }, [roles, defaults]);
}
